import type { DeliveryTrackerDocument } from '@features/organization/components/delivery-tracker-detail/types/delivery-tracker-document.types';
import { KnownContentTypeMap } from '@kerberos-compliance/compliance-shared-ts/core/storage/enums/known-content-type.enum';
import { KnownDataTypeMap } from '@kerberos-compliance/compliance-shared-ts/core/storage/enums/known-data-type.enum';
import type { DeliveryTrackerStatus } from '@kerberos-compliance/compliance-shared-ts/features/delivery/enums/delivery-tracker-status.enum';
import { DeliveryTrackerStatusMap } from '@kerberos-compliance/compliance-shared-ts/features/delivery/enums/delivery-tracker-status.enum';

/**
 * Whether a document is a risk analysis delivered to the tracker, the report a completed tracker must keep.
 *
 * @param {DeliveryTrackerDocument} document - The document to check.
 * @returns {boolean} True for a delivered risk analysis.
 */
function isDeliveredRiskAnalysis(document: DeliveryTrackerDocument): boolean {
  return (
    document.knownDataType === KnownDataTypeMap.DeliveryTrackerResult &&
    document.knownContentType === KnownContentTypeMap.RiskAnalysis
  );
}

export const DeliveryTrackerDocumentDeleteStateMap = {
  hidden: 'hidden',
  enabled: 'enabled',
  lastRiskAnalysis: 'lastRiskAnalysis',
} as const;

export type DeliveryTrackerDocumentDeleteState =
  (typeof DeliveryTrackerDocumentDeleteStateMap)[keyof typeof DeliveryTrackerDocumentDeleteStateMap];

/**
 * Decides how one document row offers its delete action. Mirrors the API's guard so the agent sees upfront what
 * the API would reject: only delivered results can be deleted, and a completed tracker keeps its last risk
 * analysis until a replacement was uploaded. The API stays the authority - a stale list is still caught there.
 *
 * @param {DeliveryTrackerStatus} trackerStatus - The status of the tracker the table belongs to.
 * @param {DeliveryTrackerDocument[]} documents - Every document currently listed for the tracker.
 * @param {DeliveryTrackerDocument} document - The row to decide for.
 * @returns {DeliveryTrackerDocumentDeleteState} How the row's delete action is offered.
 */
export function getDeliveryTrackerDocumentDeleteState(
  trackerStatus: DeliveryTrackerStatus,
  documents: readonly DeliveryTrackerDocument[],
  document: DeliveryTrackerDocument,
): DeliveryTrackerDocumentDeleteState {
  if (document.knownDataType !== KnownDataTypeMap.DeliveryTrackerResult) {
    return DeliveryTrackerDocumentDeleteStateMap.hidden;
  }

  const isGuardedReport = trackerStatus === DeliveryTrackerStatusMap.Complete && isDeliveredRiskAnalysis(document);
  if (isGuardedReport && documents.filter((listed) => isDeliveredRiskAnalysis(listed)).length <= 1) {
    return DeliveryTrackerDocumentDeleteStateMap.lastRiskAnalysis;
  }

  return DeliveryTrackerDocumentDeleteStateMap.enabled;
}
